import React, { useState, useRef, useEffect } from 'react';
import { transcribeAudio } from '../../api';

const AudioRecorder = ({ onTranscriptionComplete, onRecordingStateChange, disabled = false, language = 'en' }) => {
  const [isRecording, setIsRecording] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [recordingTime, setRecordingTime] = useState(0);
  const [audioLevel, setAudioLevel] = useState(0);
  const [error, setError] = useState(null);
  
  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);
  const recordingIntervalRef = useRef(null);
  const audioContextRef = useRef(null);
  const analyserRef = useRef(null);
  const microphoneRef = useRef(null);
  const animationFrameRef = useRef(null);


  // Start recording
  const startRecording = async () => {
    try {
      setError(null);
      audioChunksRef.current = [];
      
      // Get microphone access
      const stream = await navigator.mediaDevices.getUserMedia({ 
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          sampleRate: 16000,
          channelCount: 1
        } 
      });
      
      // Set up audio context for level monitoring
      audioContextRef.current = new (window.AudioContext || window.webkitAudioContext)();
      analyserRef.current = audioContextRef.current.createAnalyser();
      analyserRef.current.fftSize = 256;
      
      microphoneRef.current = audioContextRef.current.createMediaStreamSource(stream);
      microphoneRef.current.connect(analyserRef.current);
      
      // Set up media recorder - prefer WAV for better compatibility
      const supportedTypes = [
        'audio/wav',
        'audio/webm;codecs=opus',
        'audio/webm',
        'audio/mp4',
        'audio/ogg;codecs=opus'
      ];
      
      let mimeType = null;
      for (const type of supportedTypes) {
        if (MediaRecorder.isTypeSupported(type)) {
          mimeType = type;
          console.log(`✅ Using audio format: ${type}`);
          break;
        }
      }
      
      if (!mimeType) {
        throw new Error('No supported audio format found');
      }
      
      mediaRecorderRef.current = new MediaRecorder(stream, {
        mimeType: mimeType
      });
      
      mediaRecorderRef.current.ondataavailable = (event) => {
        if (event.data.size > 0) {
          audioChunksRef.current.push(event.data);
        }
      };
      
      mediaRecorderRef.current.onstop = async () => {
        // Convert to WAV format for Whisper
        const audioBlob = new Blob(audioChunksRef.current, { type: 'audio/webm' });
        await processRecording(audioBlob);
        
        // Stop all tracks
        stream.getTracks().forEach(track => track.stop());
      };
      
      // Start recording
      mediaRecorderRef.current.start(100); // Collect data every 100ms
      setIsRecording(true);
      setRecordingTime(0);
      
      // Notify parent component
      if (onRecordingStateChange) {
        onRecordingStateChange(true, 0);
      }
      
      // Start timer
      recordingIntervalRef.current = setInterval(() => {
        setRecordingTime(prev => prev + 1);
      }, 1000);
      
      // Start audio level monitoring
      updateAudioLevel();
      
    } catch (err) {
      console.error('Failed to start recording:', err);
      setError('Failed to access microphone. Please check permissions.');
      // Clean up any partial setup
      if (audioContextRef.current) {
        audioContextRef.current.close();
        audioContextRef.current = null;
      }
      if (mediaRecorderRef.current) {
        mediaRecorderRef.current = null;
      }
    }
  };

  // Stop recording
  const stopRecording = () => {
    if (mediaRecorderRef.current && isRecording) {
      mediaRecorderRef.current.stop();
      setIsRecording(false);
      setIsProcessing(true);
      
      // Notify parent component
      if (onRecordingStateChange) {
        onRecordingStateChange(false, 0);
      }
      
      // Clean up timers and animation frames
      if (recordingIntervalRef.current) {
        clearInterval(recordingIntervalRef.current);
        recordingIntervalRef.current = null;
      }
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
        animationFrameRef.current = null;
      }
    }
  };

  // Process the recorded audio
  const processRecording = async (audioBlob) => {
    try {
      console.log('🎤 Processing audio recording...');
      console.log('📊 Audio blob details:', {
        size: audioBlob.size,
        type: audioBlob.type,
        lastModified: audioBlob.lastModified
      });
      
      // DEBUG: Save audio file locally for debugging
      await saveAudioForDebugging(audioBlob);
      
              // Send to backend for transcription with selected language
        console.log('📡 Calling transcribeAudio with language:', language);
              const result = await transcribeAudio(audioBlob, language);
      console.log('📡 transcribeAudio result:', result);
      
      if (result && result.text && result.text.trim()) {
        console.log('✅ Transcription successful:', result.text);
        console.log('📝 Calling onTranscriptionComplete...');
        onTranscriptionComplete(result.text, result.confidence);
      } else {
        console.log('⚠️ No text in transcription result:', result);
        setError('No speech detected. Please try again.');
      }
      
    } catch (err) {
      console.error('❌ Transcription failed:', err);
      console.error('❌ Error details:', {
        message: err.message,
        stack: err.stack
      });
      setError('Failed to transcribe audio. Please try again.');
    } finally {
      setIsProcessing(false);
    }
  };

  // Save audio file for debugging
  const saveAudioForDebugging = async (audioBlob) => {
    try {
      const timestamp = new Date().toISOString().replace(/[:.]/g, '-');
      
      // Always save as WAV for better compatibility
      const filename = `debug_audio_${timestamp}.wav`;
      
      // Send to backend to save locally
      const formData = new FormData();
      formData.append('audio', audioBlob, filename);
      formData.append('save_debug', 'true');
      
      const response = await fetch('/api/esper/audio/save_audio_debug', {
        method: 'POST',
        body: formData
      });
      
      if (response.ok) {
        const result = await response.json();
        console.log('💾 Audio saved locally:', result.filename);
      } else {
        console.error('Failed to save audio locally');
      }
    } catch (err) {
      console.error('Failed to save audio for debugging:', err);
    }
  };



  // Update audio level for visual feedback
  const updateAudioLevel = () => {
    if (!analyserRef.current || !isRecording) return;
    
    const dataArray = new Uint8Array(analyserRef.current.frequencyBinCount);
    analyserRef.current.getByteFrequencyData(dataArray);
    
    // Calculate average level
    const average = dataArray.reduce((a, b) => a + b) / dataArray.length;
    setAudioLevel(average);
    
    // Notify parent component of audio level
    if (onRecordingStateChange) {
      onRecordingStateChange(true, average);
    }
    
    animationFrameRef.current = requestAnimationFrame(updateAudioLevel);
  };

  // Format recording time
  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (recordingIntervalRef.current) {
        clearInterval(recordingIntervalRef.current);
      }
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
      if (audioContextRef.current) {
        audioContextRef.current.close();
      }
      if (mediaRecorderRef.current) {
        mediaRecorderRef.current.stop();
      }
    };
  }, []);

  // Auto-stop after 60 seconds
  useEffect(() => {
    if (recordingTime >= 60 && isRecording) {
      stopRecording();
    }
  }, [recordingTime, isRecording]);

  const getButtonClass = () => {
    if (disabled) return 'audio-btn disabled';
    if (isProcessing) return 'audio-btn processing';
    if (isRecording) return 'audio-btn recording';
    return 'audio-btn';
  };

  const getButtonIcon = () => {
    if (isProcessing) return '⏳';
    if (isRecording) return '⏹️';
    return '🎤';
  };

  const getButtonSvg = () => {
    if (isProcessing) {
      return (
        <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
          <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z"/>
        </svg>
      );
    }
    if (isRecording) {
      return (
        <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
          <path d="M6 6h12v12H6z"/>
        </svg>
      );
    }
    return (
      <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
        <path d="M12 14c1.66 0 3-1.34 3-3V5c0-1.66-1.34-3-3-3S9 3.34 9 5v6c0 1.66 1.34 3 3 3zm5.91-3c-.49 0-.9.36-.98.85C16.52 14.2 14.47 16 12 16s-4.52-1.8-4.93-4.15c-.08-.49-.49-.85-.98-.85-.61 0-1.09.54-1 1.14.49 3 2.89 5.35 5.91 5.78V20c0 .55.45 1 1 1s1-.45 1-1v-2.08c3.02-.43 5.42-2.78 5.91-5.78.1-.6-.39-1.14-1-1.14z"/>
      </svg>
    );
  };

  return (
    <div className="audio-recorder">
      <div className="audio-controls">
        {/* Main recording button */}
        <button
          className={getButtonClass()}
          onClick={isRecording ? stopRecording : startRecording}
          disabled={disabled}
          title={isRecording ? 'Stop recording' : 'Start voice input'}
        >
          <span className="audio-icon">{getButtonSvg()}</span>
        </button>
      </div>
      
      {error && (
        <div className="audio-error">
          <span className="error-icon">⚠️</span>
          <span className="error-text">{error}</span>
        </div>
      )}
    </div>
  );
};

export default AudioRecorder;
